import { VdsButton, VdsNotification } from '@volt/volt-extended-components-react';
import { PropsWithChildren, ReactElement } from 'react';
import { DevTool } from '@hookform/devtools';
import { useTranslation } from 'react-i18next';
import { DrawerForm } from '@dmc/ui';
import { Control } from 'react-hook-form';
import { useFormEditMode } from './useFormEditMode';

export interface ModelFormControlProps<TFormModel extends object> {
  canSubmit: boolean;
  onSubmit: () => void;
  errorMessage?: string;
  control: Control<TFormModel, unknown, TFormModel>;
}

interface ModelFormProps<TFormModel extends object> extends ModelFormControlProps<TFormModel> {
  submitLabel?: string;
  editMode?: boolean;
}

export const ModelForm = <TFormModel extends object>({
  canSubmit,
  onSubmit,
  errorMessage,
  control,
  submitLabel,
  editMode: editModeOverride,
  children,
}: PropsWithChildren<ModelFormProps<TFormModel>>): ReactElement => {
  const { t } = useTranslation();

  const editMode = useFormEditMode(editModeOverride);

  return (
    <>
      <DrawerForm
        footer={
          <VdsButton
            variant="primary"
            size="medium"
            disabled={!canSubmit}
            isFullWidth
            onClick={onSubmit}
          >
            {submitLabel ?? (editMode ? t('SaveChanges') : t('Create'))}
          </VdsButton>
        }
      >
        <>
          {typeof errorMessage !== 'undefined' && (
            <VdsNotification variant="inline" status="danger" showCloseButton={false}>
              <div slot="header">{t('Error')}</div>
              <span slot="body">{errorMessage}</span>
            </VdsNotification>
          )}

          {children}
        </>
      </DrawerForm>

      <DevTool control={control} styles={{ panel: { height: '90vh' } }} placement="bottom-left" />
    </>
  );
};

export default ModelForm;
